import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import BudgetProfileLayout from './BudgetProfileLayout';

// import utility file
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { ExclamationTriangleFill } from 'react-bootstrap-icons';

import AnneescolaireService from '../../services/AnneescolaireService';
import BudgetService from '../../services/BudgetService';

const ViewBudget = () => {
	const { id } = useParams();
	const [budget, setBudget] = useState(null); // Changed this from "year" to "budget"
	const [yearOptions, setYearOptions] = useState([]);
	const [validated, setValidated] = useState(false);
	const [feedbackMessages, setFeedbackMessages] = useState({});
	const [showAlert, setShowAlert] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');
	const [selectedYear, setSelectedYear] = useState([]);
	const [selectedCategorie, setSelectedCategorie] = useState('');

	const CATEGORIE_CHOICES = [
		{ value: 'frais_scolarite', label: 'Frais de Scolarité' },
		{ value: 'subventions_dons', label: 'Subventions et Dons' },
		{ value: 'depenses_exploitation', label: "Dépenses d'Exploitation" },
		{ value: 'investissements', label: 'Investissements' },
		{ value: 'reserve_urgence', label: "Réserve d'Urgence" },
		{
			value: 'provisions_annee_suivante',
			label: "Provisions pour l'année suivante"
		},
		{ value: 'depenses_educatives', label: 'Dépenses Éducatives' },
		{ value: 'couts_administratifs', label: 'Coûts Administratifs' },
		{
			value: 'depenses_services_publics',
			label: 'Dépenses pour les Services Publics'
		},
		{ value: 'depenses_securite', label: 'Dépenses pour la Sécurité' }
	];

	useEffect(() => {
		const fetchYearOptions = async () => {
			try {
				const years = await AnneescolaireService.getYearOptions();
				setYearOptions(years);
			} catch (error) {
				console.error('Error fetching years options', error);
			}
		};

		fetchYearOptions();
	}, []);

	useEffect(() => {
		Promise.all([
			BudgetService.getBudget(id),
			AnneescolaireService.getAllAnneescolaire()
		]).then(([budgetData]) => {
			setBudget(budgetData.data);

			if (budgetData && budgetData.data) {
				const budgetYear = budgetData.data.anneescolaire;
				const budgetCategorie = budgetData.data.categorie;
				setSelectedYear(budgetYear);
				setSelectedCategorie(budgetCategorie);
			}
		});
	}, [id]);

	const YEAR_CHOICES = yearOptions;

	const handleSubmit = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;

		if (form.checkValidity() === false) {
			event.stopPropagation();
		} else {
			try {
				const budgetData = new FormData();

				// Add all your form data to the FormData
				budgetData.append('categorie', form.categorie.value);
				budgetData.append('prevision_depense', form.prevision_depense.value);
				budgetData.append('prevision_recette', form.prevision_recette.value);
				budgetData.append('description', form.description.value);
				budgetData.append('anneescolaire', form.anneescolaire.value);

				// Call the AnneescolaireService to create the year
				const response = await BudgetService.updateBudget(id, budgetData);

				// Reset the form and state variables after successful update
				form.reset();
				setValidated(false);
				setFeedbackMessages({});

				// Add this line to refresh the page
				window.location.reload();
			} catch (error) {
				console.error(
					'Erreur lors de la mise à jour des informations du budget',
					error
				);

				if (error.response && error.response.data) {
					const apiErrors = error.response.data;

					// If there are field-specific errors
					if (typeof apiErrors === 'object' && !Array.isArray(apiErrors)) {
						const newFeedbackMessages = {};

						for (const field in apiErrors) {
							newFeedbackMessages[field] = apiErrors[field][0];
						}

						setFeedbackMessages(newFeedbackMessages);
					}

					// If there's a general error detail message
					if (apiErrors.detail) {
						setAlertMessage(apiErrors.detail[0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					} else if (
						apiErrors['Il ya erreur'] &&
						apiErrors['Il ya erreur'][0]
					) {
						setAlertMessage(apiErrors['Il ya erreur'][0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					}
				} else {
					alert(
						"Une erreur s'est produite lors de la soumission du formulaire."
					);
				}
			}
		}

		setValidated(true);
	};

	return (
		<BudgetProfileLayout>
			{budget && (
				<>
					<Card className="mb-3 ">
						{/* Card body */}
						<Card.Body>
							{/*  Form  */}
							<Form
								className="mb-3"
								noValidate
								validated={validated}
								onSubmit={handleSubmit}
							>
								{showAlert && (
									<Alert variant="danger">
										<ExclamationTriangleFill size={25} className="me-1" />
										{alertMessage}
									</Alert>
								)}

								<Row>
									{/* Categorie */}
									<Col xs={12} className="mb-3">
										<Form.Label htmlFor="categorie">
											Catégorie <span className="text-danger">*</span>
										</Form.Label>
										<Form.Select
											name="categorie"
											required
											value={selectedCategorie}
											isInvalid={!!feedbackMessages['categorie']}
											onChange={(e) => {
												const value = e.target.value;
												setSelectedCategorie(value);
											}}
										>
											<option value="">Selectionner la catégorie</option>
											{CATEGORIE_CHOICES.map((choice, index) => (
												<option key={index} value={choice.value}>
													{choice.label}
												</option>
											))}
										</Form.Select>
										<Form.Control.Feedback type="invalid">
											{feedbackMessages['categorie']}
										</Form.Control.Feedback>
									</Col>

									<Col xs={12} className="mb-3">
										{/* Dépenses et Recettes  */}
										<Form.Group>
											<Row>
												<Col md={6} sm={12} className="mb-3">
													<Form.Label htmlFor="prevision_depense">
														Prévision dépenses{' '}
														<span className="text-danger">*</span>
													</Form.Label>
													<Form.Control
														name="prevision_depense"
														type="number"
														min="25"
														placeholder="123456789 FCFA"
														id="prevision_depense"
														required
														isInvalid={!!feedbackMessages['prevision_depense']}
														onChange={(e) => {
															setFeedbackMessages((prevMessages) => ({
																...prevMessages,
																prevision_depense: e.target.value
																	? ''
																	: 'La valeur prévisionnelle des dépenses est requise.'
															}));
														}}
														defaultValue={budget.prevision_depense || ''}
													/>
													<Form.Control.Feedback type="invalid">
														{feedbackMessages['prevision_recette']}
													</Form.Control.Feedback>
												</Col>
												<Col md={6} sm={12} className="mb-3">
													<Form.Label htmlFor="prevision_recette">
														Prévision recettes{' '}
														<span className="text-danger">*</span>
													</Form.Label>
													<Form.Control
														name="prevision_recette"
														type="number"
														min="25"
														placeholder="123456789 FCFA"
														id="prevision_recette"
														required
														isInvalid={!!feedbackMessages['prevision_recette']}
														onChange={(e) => {
															setFeedbackMessages((prevMessages) => ({
																...prevMessages,
																prevision_recette: e.target.value
																	? ''
																	: 'La valeur prévisionnelle des recettes est requise.'
															}));
														}}
														defaultValue={budget.prevision_recette || ''}
													/>
													<Form.Control.Feedback type="invalid">
														{feedbackMessages['prevision_recette']}
													</Form.Control.Feedback>
												</Col>
											</Row>
										</Form.Group>
									</Col>

									{/* Description */}
									<Col xs={12} className="mb-3">
										<Form.Group className="mb-3">
											<Form.Label htmlFor="description">Description</Form.Label>
											<Form.Control
												name="description"
												as="textarea"
												placeholder="Brêve description de la prévision budgétaire"
												id="description"
												style={{ height: '100px' }}
												defaultValue={budget.description || ''}
											/>
											<Form.Control.Feedback type="invalid">
												{feedbackMessages['description']}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>

									{/* Année scolaire */}
									<Col xs={12} className="mb-3">
										<Form.Label htmlFor="anneescolaire">
											Année scolaire
										</Form.Label>
										<Form.Select
											name="anneescolaire"
											required
											value={selectedYear}
											isInvalid={!!feedbackMessages['anneescolaire']}
											onChange={(e) => {
												const value = e.target.value;
												setSelectedYear(value);
											}}
										>
											<option value="">Selectionner l'année scolaire</option>
											{YEAR_CHOICES.map((choice, index) => (
												<option key={index} value={choice.value}>
													{choice.label}
												</option>
											))}
										</Form.Select>
										<Form.Control.Feedback type="invalid">
											{feedbackMessages['anneescolaire']}
										</Form.Control.Feedback>
									</Col>

									{/* Buttons */}
									<Col xs={12} className="mt-4">
										<Button variant="success" type="submit">
											Mettre à jour
										</Button>
										<Button
											type="reset"
											variant="outline-danger"
											className="ms-2"
										>
											Effacer
										</Button>
									</Col>
								</Row>
							</Form>
						</Card.Body>
					</Card>
				</>
			)}
		</BudgetProfileLayout>
	);
};

export default ViewBudget;
